import { useCallback, useRef, useState } from 'react';
import { searchReportsApi } from '@intelligence/services/reportService';
import type { IntelligenceReportDataInfo } from '../types';
import { REPORT_SEARCH_SYSTEMS } from '../_constants';
import { createReportFieldUpdater } from './helpers';
import type { ISearchParams, ISearchResponse, TReportSearchError } from './types';

export const useReportSearch = () => {
  const [results, setResults] = useState<IntelligenceReportDataInfo[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<TReportSearchError>('');
  const latestKeywordRef = useRef<string>('');

  const search = useCallback(async (params: ISearchParams): Promise<void> => {
    const keyword = params.keyword?.trim() || '';
    latestKeywordRef.current = keyword;
    if (!keyword) {
      setResults([]);
      setLoading(false);
      setError('');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await searchReportsApi({
        ai_keyword: keyword,
        systems: REPORT_SEARCH_SYSTEMS,
      }) as ISearchResponse;
      if (latestKeywordRef.current !== keyword) return;
      const list = Array.isArray(res?.resource_list) ? res.resource_list : [];
      setResults(list);
    } catch (err: unknown) {
      console.error('[ReportSearch] search failed:', err);
      if (latestKeywordRef.current !== keyword) return;
      setResults([]);
      setError('request_failed');
    } finally {
      if (latestKeywordRef.current === keyword) {
        setLoading(false);
      }
    }
  }, []);

  const reset = useCallback((): void => {
    latestKeywordRef.current = '';
    setResults([]);
    setLoading(false);
    setError('');
  }, []);

  const updateReportField = useCallback(createReportFieldUpdater(setResults), []);

  return { results, loading, error, search, reset, updateReportField };
};
